import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { NodeViewProps } from '@tiptap/react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';

import { BlockInstructionStatus, useBlockInstanceActions } from '@/hooks/use-block-instance-actions';

import { externalWorkspaceTabStateAtom } from '@/state';

import styles from '../block-node-view.module.scss';

export const BlockNodeViewMenus = (props: BlockNodeViewMenusProps) => {
  const { blockId, onMenuOpen, onMenuClose, nodeViewProps, blockInstructionStatus } = props;

  const setExternalWorkspaceTab = useSetAtom(externalWorkspaceTabStateAtom);

  const {
    isOutOfSync,
    hasMasterBlock,
    editMasterBlock,
    deleteBlockNode,
    copyBlockContents,
    convertBlockToText,
    revertToMasterBlock,
    pushBlockChangesToMaster,
    createNewBlockFromExisting,
  } = useBlockInstanceActions(nodeViewProps);

  const menu = [
    {
      icon: 'edit-02',
      onClick: editMasterBlock,
      name: 'Edit Master Block',
      disabled: !hasMasterBlock,
    },
    {
      type: 'divider',
    },
    {
      icon: 'copy-02',
      name: 'Copy Contents',
      onClick: copyBlockContents,
    },
    {
      type: 'divider',
    },
    {
      icon: 'text-circle',
      onClick: convertBlockToText,
      name: 'Convert Block to Text',
    },
    {
      type: 'divider',
    },
    {
      danger: true,
      name: 'Delete',
      icon: 'delete-01',
      onClick: deleteBlockNode,
    },
  ];

  const outOfSyncMenu = [
    {
      icon: 'arrow-up-right-01',
      onClick: pushBlockChangesToMaster,
      name: 'Push Changes to Master Block',
    },
    {
      type: 'divider',
    },
    {
      disabled: !hasMasterBlock,
      icon: 'arrow-down-left-01',
      onClick: revertToMasterBlock,
      name: 'Revert to Master Block',
    },
    {
      type: 'divider',
    },
    {
      icon: 'artboard-tool',
      onClick: createNewBlockFromExisting,
      name: 'Create a New Block from this',
    },
  ];

  return (
    <div className={styles.blockMenusAndStatuses}>
      {isOutOfSync && (
        <Popover menu={outOfSyncMenu} placement="bottom" onClose={onMenuClose} offset={[0, 10]}>
          <span className={clsx(styles.outOfSyncMenu, 'block-menu')}>
            <Icon size={14} onMouseDown={onMenuOpen} data-testid="out-of-sync-menu" type="square-arrow-data-transfer-diagonal" />
            <span>Out of sync</span>
          </span>
        </Popover>
      )}
      {blockInstructionStatus && (
        <Tooltip content={blockInstructionStatus.message}>
          <span
            className={styles.instructionStatus}
            data-testid="block-instruction-status"
            onClick={() => {
              setExternalWorkspaceTab({
                tab: 'aiContext',
                focusedInstructionGroupId: blockId,
              });
            }}
          >
            <Icon
              size={14}
              type={blockInstructionStatus.icon}
              data-testid={`block-instruction-${blockInstructionStatus.status}`}
              multiColor={blockInstructionStatus.status === 'partially-enabled'}
            />
          </span>
        </Tooltip>
      )}
      <Popover menu={menu} placement="bottom" onClose={onMenuClose} offset={[0, 10]}>
        <Icon
          type="more-horizontal"
          data-testid="main-menu"
          className={clsx(styles.moreMenu, 'block-menu')}
          onMouseDown={onMenuOpen}
        />
      </Popover>
    </div>
  );
};

interface BlockNodeViewMenusProps {
  blockId: string;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  nodeViewProps: NodeViewProps;
  blockInstructionStatus: BlockInstructionStatus | null;
}
